// Keep retrying the requested resource instead of exhausting retries and
// allowing the stream controller to switch or skip failed fragments.
export const createPersistentHLSLoadPolicies = (defaults, overrides = {}) => {
  const retry = {
    maxNumRetry: Infinity,
    retryDelayMs: 1000,
    maxRetryDelayMs: 8000,
    shouldRetry: () => true,
  };

  return Object.fromEntries(
    [
      "manifestLoadPolicy",
      "playlistLoadPolicy",
      "fragLoadPolicy",
      "keyLoadPolicy",
      "certLoadPolicy",
    ].map((name) => {
      const policy = {
        ...defaults[name]?.default,
        ...overrides[name]?.default,
      };
      const maxLoadTimeMs = Math.min(policy.maxLoadTimeMs ?? Infinity, 30000);
      return [
        name,
        {
          default: {
            ...policy,
            // A pending request gets a finite lifetime even though the overall
            // loading state has no deadline. Respect stricter custom limits.
            maxTimeToFirstByteMs: Math.min(
              policy.maxTimeToFirstByteMs ?? Infinity,
              10000,
              maxLoadTimeMs
            ),
            maxLoadTimeMs,
            errorRetry: { ...retry },
            timeoutRetry: { ...retry },
          },
        },
      ];
    })
  );
};

// Restart the resource request, not the media pipeline. This also handles a
// stalled initial manifest, for which hls.startLoad() cannot reload the source.
export const createRecoverableHLSLoader = (BaseLoader) =>
  class RecoverableHLSLoader {
    constructor(config) {
      this.hlsConfig = config;
      this.loader = new BaseLoader(config);
      this.pending = false;
      this.lastRestartAt = -Infinity;
      this.handleOnline = () => {
        if (!this.pending || Date.now() - this.lastRestartAt < 750) return;
        this.lastRestartAt = Date.now();
        this.loader.destroy();
        this.loader = new BaseLoader(this.hlsConfig);
        this.startRequest();
      };
    }

    get stats() {
      return this.loader.stats;
    }
    get context() {
      return this.loader.context;
    }

    load(context, config, callbacks) {
      this.request = { context, config, callbacks };
      this.pending = true;
      if (typeof window !== "undefined")
        window.addEventListener("online", this.handleOnline);
      this.startRequest();
    }

    startRequest() {
      const { context, config, callbacks } = this.request;
      const activeLoader = this.loader;
      const wrapped = { ...callbacks };
      for (const event of ["onSuccess", "onError", "onTimeout", "onAbort"]) {
        wrapped[event] = (...args) => {
          if (!this.pending || activeLoader !== this.loader) return;
          this.stopListening();
          callbacks[event]?.(...args);
        };
      }
      wrapped.onProgress = (...args) => {
        if (this.pending && activeLoader === this.loader)
          callbacks.onProgress?.(...args);
      };
      activeLoader.load(context, { ...config }, wrapped);
    }

    stopListening() {
      this.pending = false;
      if (typeof window !== "undefined")
        window.removeEventListener("online", this.handleOnline);
    }

    abort() {
      this.loader.abort();
      this.stopListening();
    }

    destroy() {
      this.stopListening();
      this.loader.destroy();
      this.request = null;
    }

    getCacheAge() {
      return this.loader.getCacheAge?.() ?? null;
    }
    getResponseHeader(name) {
      return this.loader.getResponseHeader?.(name) ?? null;
    }
  };
